import { declareAiCapability, provideAiCapability } from "./ai-capability-registry";

/**
 * A tab telling the omnibar it is somewhere to go.
 *
 * The left hand navigation offers one destination per entry, so a tabbed page arrives as a single
 * place and lands on whichever tab it opens with. The tabs behind it are states of their own, and
 * the only thing that knows a tab exists - its heading, the permission it needs, the filters that
 * live on it - is the tab's own file. So the tab declares itself, beside the capabilities it
 * already declares, and no list of tabs is kept anywhere else. The settings panel then lists it
 * like any other declaration, without anything having to enumerate destinations.
 *
 * A tab a page opens with needs no declaration: navigating to the page already lands there.
 *
 * `opens` should be the tab's own space - `page:certificationPeriods` rather than `page` - and the
 * filters on that tab name the same space as their `scope`. They are then reachable after this and
 * nothing else. A tab that shares its screen, and so its filters, with a sibling opens plain `page`
 * instead.
 */
export type AiTabDestination = {
  /** Conventionally `<page>.open`, beside the `<page>.filter` that lives on it. */
  id: string;
  /** How the panel and the planner name it, e.g. "Open the Certification Periods tab". */
  title: string;
  stateName: string;
  /** The space the tab opens, which its own capabilities name as their scope. */
  opens: string;
  description: string;
  location: string;
  permissionKey: string;
  examples?: string[];
};

type Runnable = { id: string; stateName: string };

const declared: Runnable[] = [];
const provided = new Set<string>();

/** Set once the app has a router. Declarations made before that wait for it. */
let goToState: ((stateName: string) => void) | null = null;

export function declareAiTabDestination(destination: AiTabDestination): void {
  declareAiCapability({
    id: destination.id,
    title: destination.title,
    effect: "navigate",
    description: destination.description,
    location: destination.location,
    permissionKey: destination.permissionKey,
    opens: [destination.opens],
    params: [],
    examples: destination.examples ?? [],
  });

  const runnable = { id: destination.id, stateName: destination.stateName };

  declared.push(runnable);
  provide(runnable);
}

function provide(runnable: Runnable): void {
  if (goToState === null || provided.has(runnable.id)) {
    return;
  }

  provided.add(runnable.id);
  provideAiCapability(runnable.id, () => goToState?.(runnable.stateName));
}

/**
 * Hands them the router.
 *
 * One runner for all of them rather than each tab providing its own: a runner that only existed
 * while its tab was open could never be the thing that opens it.
 */
export function provideAiTabDestinationRunners(go: (stateName: string) => void): void {
  goToState = go;
  declared.forEach(provide);
}
